import React, { useState, useContext } from 'react'
import { Modal, Table, Spin, Button, Input, message } from 'antd'
import moment from 'moment'
import { EditOutlined } from '@ant-design/icons'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserSave } from 'utils/User'

const AvailabilityLogsModal = ({
  visible,
  onCancel,
  logs,
  loading,
  onEdit,
}) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const hasEdit = canUserEdit(userPermissions || currentUser, isClient)
  const hasSave = canUserSave(userPermissions || currentUser, isClient)
  const canEditLog = hasEdit && hasSave

  const [editingLog, setEditingLog] = useState(null)
  const [newReason, setNewReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const columns = [
    {
      title: 'Date and time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => moment(value).format('Do MMMM YYYY, h:mm:ss a'),
    },
    {
      title: 'Changed by',
      dataIndex: ['changedBy'],
      key: 'changedBy',
      render: (changedBy) =>
        `${changedBy?.firstName || ''} ${changedBy?.lastName || ''}`.trim(),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (reason) => reason || '—',
    },
    ...(hasEdit
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <EditOutlined
                style={{ marginRight: 12, cursor: 'pointer' }}
                onClick={() => {
                  setEditingLog(record)
                  setNewReason(record.reason || '')
                }}
              />
            ),
          },
        ]
      : []),
  ]

  const handleEditSave = async () => {
    if (!canEditLog) return
    if (!newReason.trim()) {
      message.warning('Reason cannot be empty.')
      return
    }

    try {
      setSubmitting(true)
      await onEdit(editingLog.id, newReason.trim()) // Call passed-in mutation handler
      message.success('Reason updated successfully')
      setEditingLog(null)
    } catch (err) {
      console.error(err)
      message.error('Failed to update reason.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal
        title='Availability Logs'
        visible={visible}
        onCancel={onCancel}
        footer={null}
        width={800}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : (
          <Table
            dataSource={logs}
            columns={columns}
            rowKey='id'
            pagination={{ pageSize: 10 }}
          />
        )}
      </Modal>

      {/* Edit Reason Modal */}
      <Modal
        title='Edit Reason'
        visible={!!editingLog}
        onCancel={() => setEditingLog(null)}
      >
        <Input.TextArea
          rows={4}
          value={newReason}
          style={{ marginBottom: 8 }}
          onChange={(e) => setNewReason(e.target.value)}
        />
        <div style={{ textAlign: 'right' }}>
          <Button
            onClick={() => setEditingLog(null)}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={handleEditSave}
            loading={submitting}
            disabled={!canEditLog}
          >
            Save
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default AvailabilityLogsModal
